import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import useGiftCardShopData from './useGiftCardShopData';
import GiftCardPreview from './GiftCardPreview';
import { giftCardService } from '../../_services/giftCard.service';
import './GiftCardShop.css';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

// GiftCardConfirmation — the post-payment screen. Stripe returns the customer
// here. It shows the designed card and the outcome (immediate code, gift sent,
// or scheduled).
//
// CTO INTEGRATION: in production, configure Stripe to return to this route with
// an order id, then fetch the real order (code + status) here. For now it reads
// descriptive query params so the screen is reviewable end-to-end:
//   ?amount=25&designId=editorial-birthday&to=Alex&gift=1&schedule=2026-06-01&code=BEU-...
const GiftCardConfirmation = () => {
  const { salonSlug, salonId } = useParams();
  const salonIdOrSlug = salonSlug || salonId;
  const history = useHistory();
  const { search } = useLocation();
  const { loading, salonName, currency, designs } = useGiftCardShopData(salonIdOrSlug);

  const params = useMemo(() => new URLSearchParams(search), [search]);

  // Stripe returns here with ?tid=<transactionId>. Fetch the real order so the
  // screen shows the issued code + outcome. Falls back to descriptive query
  // params (dev / no tid) so it stays reviewable end-to-end.
  const tid = params.get('tid');
  const [order, setOrder] = useState(null);
  useEffect(() => {
    if (!tid) return undefined;
    let alive = true;
    giftCardService.getGiftCardOrder(tid)
      .then((o) => { if (alive && o) setOrder(o); })
      .catch((e) => console.log('[GiftCardConfirmation] order lookup failed:', e && e.message));
    return () => { alive = false; };
  }, [tid]);

  const amount = (order && order.faceValue) || parseInt(params.get('amount'), 10) || 0;
  const designId = (order && order.designId) || params.get('designId');
  const recipientName = (order && order.recipientName) || params.get('to') || '';
  const isGift = order ? !!order.isGift : params.get('gift') === '1';
  const scheduleDate = (order && order.deliverAt) || params.get('schedule') || '';
  const code = (order && order.code) || params.get('code') || '';

  const design = useMemo(
    () => designs.find(d => d.id === designId) || designs[0] || null,
    [designs, designId]
  );

  const [copied, setCopied] = useState(false);
  const copyCode = () => {
    if (!code) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => {});
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = code; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
        done();
      } catch (e) { /* leave the code visible to copy manually */ }
    }
  };

  const goToSalon = () => {
    const base = salonSlug ? `/book/${salonSlug}` : `/salon/${salonIdOrSlug}`;
    history.push(base);
  };

  let headline = 'Thank you';
  let sub = 'Your gift card and receipt are on their way to your inbox.';
  if (isGift && scheduleDate) {
    headline = 'Gift scheduled';
    sub = `We will email ${recipientName || 'your recipient'} their gift card on ${fmtDate(scheduleDate)}.`;
  } else if (isGift) {
    headline = 'Gift sent';
    sub = `We have emailed ${recipientName || 'your recipient'} their gift card.`;
  }

  if (loading) {
    return (
      <div className="gcs-state">
        <div className="gcs-spinner" aria-hidden />
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="gcs">
      <header className="gcs-header">
        <div className="gcs-header-title">
          <span className="gcs-header-eyebrow">{salonName}</span>
          <h1 className="gcs-header-h1">Gift cards</h1>
        </div>
      </header>

      <div className="gcs-confirm">
        <div className="gcs-confirm-icon" aria-hidden>
          <svg width="56" height="56" viewBox="0 0 48 48" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="24" cy="24" r="21" stroke="#d6f0e0" strokeWidth="3" />
            <path d="M15 24.5l6 6 12-13" stroke="#1a8a4a" strokeWidth="3" />
          </svg>
        </div>
        <h2 className="gcs-confirm-title">{headline}</h2>
        <p className="gcs-confirm-sub">{sub}</p>

        <div className="gcs-confirm-card">
          <GiftCardPreview
            design={design}
            amount={amount}
            currency={currency}
            salonName={salonName}
            recipientName={isGift ? recipientName : ''}
          />
        </div>

        {code && !isGift && (
          <div className="gcs-confirm-code">
            <span className="gcs-confirm-code-label">Your code</span>
            <span className="gcs-confirm-code-value">{code}</span>
            <button type="button" className="gcs-confirm-copy" onClick={copyCode}>
              {copied ? 'Copied ✓' : 'Copy code'}
            </button>
          </div>
        )}

        <div className="gcs-confirm-actions">
          {/* Browser print-to-PDF, scoped to the card by the @media print
              rules in GiftCardShop.css — no server-side PDF dependency. */}
          <button type="button" className="gcs-btn-ghost" onClick={() => window.print()}>Download PDF</button>
          <button type="button" className="gcs-btn" onClick={goToSalon}>
            Back to {salonName}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftCardConfirmation;
